import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PenLine, Mail, User, Bell } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SIG = (name = "", role = "IT Engineer") =>
  `Best regards,\n${name}\n${role} · KINOS IT Support\n+670 77138966 · service@kinos.tl`;

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    department: user?.department || "",
    location: user?.location || "",
    organization: user?.organization || "",
    email_signature: user?.email_signature || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        name: user.name || "",
        phone: user.phone || "",
        department: user.department || "",
        location: user.location || "",
        organization: user.organization || "",
        email_signature: user.email_signature || "",
      }));
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", form);
      setUser(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const useTemplate = () => {
    setForm((f) => ({ ...f, email_signature: DEFAULT_SIG(user?.name || "", roleLabel(user?.role)) }));
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">Settings</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="settings-heading">My profile</h1>
        <p className="text-slate-600 text-sm mt-2 max-w-lg">Update your details and your email signature. Your signature is appended to every reply you send from a ticket when the &ldquo;Email customer&rdquo; option is enabled.</p>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
          <div className="h-10 w-10 rounded-sm bg-slate-200 flex items-center justify-center">
            <User className="h-5 w-5 text-slate-600" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-sm font-medium">{user?.email}</div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">{roleLabel(user?.role)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Fld label="Full name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="settings-name" />
          </Fld>
          <Fld label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="settings-phone" placeholder="+670…" />
          </Fld>
          <Fld label="Organization">
            <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="settings-org" placeholder="KINOS HQ" />
          </Fld>
          <Fld label="Department">
            <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="settings-department" placeholder="IT Engineering" />
          </Fld>
          <Fld label="Location">
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="settings-location" placeholder="HQ Floor 3" />
          </Fld>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenLine className="h-4 w-4 text-red-600" strokeWidth={1.5} />
            <div className="font-display text-lg font-bold">Email signature</div>
          </div>
          <Button variant="outline" size="sm" onClick={useTemplate} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs" data-testid="settings-template-btn">
            Use starter template
          </Button>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Mail className="h-3 w-3" strokeWidth={1.5} />
          Appears at the bottom of every outgoing customer email you send from a ticket.
        </div>
        <Textarea
          rows={6}
          value={form.email_signature}
          onChange={(e) => setForm({ ...form, email_signature: e.target.value })}
          placeholder={`Best regards,\nYour Name\nIT Engineer · KINOS IT Support\n+670 77138966 · service@kinos.tl`}
          className="bg-white border-slate-200 rounded-sm font-mono text-xs"
          data-testid="settings-signature"
        />

        <div className="border border-dashed border-slate-200 bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Preview</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
            <span className="text-slate-600">Hi Jane, we&apos;ve rebooted the switch and your Wi-Fi should be back.</span>
            {form.email_signature && (
              <>
                <div className="my-3 h-px border-t border-dashed border-slate-200" />
                <div className="text-slate-700 whitespace-pre-wrap">{form.email_signature}</div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-500 rounded-sm h-11 px-6" data-testid="settings-save-btn">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>

      {["agent", "team_leader", "admin", "manager"].includes(user?.role) && (
        <ChimePanel />
      )}

      {["agent", "team_leader", "admin", "manager"].includes(user?.role) && (
        <MySnippetsPanel />
      )}
    </div>
  );
}

function MySnippetsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/snippets"); setItems(data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content required"); return; }
    setSaving(true);
    try {
      await api.post("/snippets", form);
      setForm({ title: "", content: "" });
      toast.success("Snippet saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this snippet?")) return;
    await api.delete(`/snippets/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="border border-slate-200 bg-white p-6 space-y-4" data-testid="my-snippets-panel">
      <div className="flex items-center gap-2">
        <PenLine className="h-4 w-4 text-red-600" strokeWidth={1.5} />
        <div className="font-display text-lg font-bold">My snippets</div>
        <span className="text-[11px] font-mono text-slate-500">personal quick-replies · only visible to you</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
        <Input placeholder="Title (e.g. VPN reset)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-200 rounded-sm h-11" data-testid="snippet-title-input" />
        <Textarea placeholder="Reply body…" rows={2} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-white border-slate-200 rounded-sm text-xs font-mono" data-testid="snippet-content-input" />
        <Button onClick={add} disabled={saving} className="bg-red-600 hover:bg-red-500 rounded-sm h-11" data-testid="snippet-add-btn">Add</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.length === 0 && <div className="text-xs text-slate-500 md:col-span-2">No snippets yet. They&apos;ll show as chips above the ticket reply composer.</div>}
        {items.map((s) => (
          <div key={s.snippet_id} className="border border-slate-200 bg-white p-3" data-testid={`snippet-item-${s.snippet_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium">{s.title}</div>
              <button onClick={() => del(s.snippet_id)} className="text-slate-500 hover:text-red-400" data-testid={`snippet-del-${s.snippet_id}`}>×</button>
            </div>
            <div className="text-[11px] text-slate-600 mt-2 whitespace-pre-wrap leading-relaxed">{s.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChimePanel() {
  const { user, setUser } = useAuth();
  const enabled = user?.chime_enabled !== false;
  const [quiet, setQuiet] = useState({ start: user?.quiet_start || "", end: user?.quiet_end || "" });
  const patch = async (body, msg) => {
    try {
      const { data } = await api.patch("/users/me", body);
      setUser(data);
      toast.success(msg);
    } catch (e) {
      toast.error("Failed to save");
    }
  };
  const toggle = (value) => patch({ chime_enabled: value }, value ? "Alert sound on" : "Alert sound muted");
  const toggleSummary = (value) => patch({ assign_summary_enabled: value }, value ? "Morning summary email on" : "Morning summary email off");
  const saveQuiet = () => {
    if (!!quiet.start !== !!quiet.end) { toast.error("Set both a start and an end time"); return; }
    patch({ quiet_start: quiet.start, quiet_end: quiet.end }, quiet.start ? `Quiet hours ${quiet.start}–${quiet.end}` : "Quiet hours cleared");
  };
  const clearQuiet = () => { setQuiet({ start: "", end: "" }); patch({ quiet_start: "", quiet_end: "" }, "Quiet hours cleared"); };
  return (
    <div className="border border-slate-200 bg-white p-6 space-y-5" data-testid="chime-panel">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-2">
        <Bell className="h-4 w-4 text-red-600 mt-1" strokeWidth={1.5} />
        <div>
          <div className="font-display text-lg font-bold">Assignment alert sound</div>
          <div className="text-xs text-slate-500 mt-1">Play a short chime when a ticket is assigned to you. Pop-ups and the bell still work when muted.</div>
        </div>
      </div>
      <Switch checked={enabled} onCheckedChange={toggle} data-testid="chime-toggle" className="data-[state=checked]:bg-red-600" />
    </div>
    <div className="border-t border-slate-200 pt-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Quiet hours</div>
      <div className="text-xs text-slate-500 mb-3">The chime mutes itself between these times every day (your device&apos;s local time). Critical alerts also stay silent.</div>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="time" value={quiet.start} onChange={(e) => setQuiet({ ...quiet, start: e.target.value })} className="w-32 bg-white border-slate-200 rounded-sm h-10" data-testid="quiet-start-input" />
        <span className="text-xs text-slate-500">to</span>
        <Input type="time" value={quiet.end} onChange={(e) => setQuiet({ ...quiet, end: e.target.value })} className="w-32 bg-white border-slate-200 rounded-sm h-10" data-testid="quiet-end-input" />
        <Button onClick={saveQuiet} className="bg-red-600 hover:bg-red-500 rounded-sm h-10" data-testid="quiet-save-btn">Save</Button>
        {user?.quiet_start && <Button variant="outline" onClick={clearQuiet} className="rounded-sm h-10 border-slate-200" data-testid="quiet-clear-btn">Clear</Button>}
      </div>
      {user?.quiet_start && <div className="text-[11px] font-mono text-red-600 mt-2" data-testid="quiet-status">Active daily {user.quiet_start}–{user.quiet_end}</div>}
    </div>
    <div className="border-t border-slate-200 pt-4 flex items-center justify-between gap-4">
      <div className="flex items-start gap-2">
        <Mail className="h-4 w-4 text-red-600 mt-1" strokeWidth={1.5} />
        <div>
          <div className="font-display text-lg font-bold">Morning summary email</div>
          <div className="text-xs text-slate-500 mt-1">Weekdays at 08:00 Dili time: a list of your open assigned tickets, most urgent first.</div>
        </div>
      </div>
      <Switch checked={user?.assign_summary_enabled !== false} onCheckedChange={toggleSummary} data-testid="summary-toggle" className="data-[state=checked]:bg-red-600" />
    </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

function roleLabel(r) {
  return ({ end_user: "End User", agent: "Support Agent", team_leader: "Team Leader", admin: "Admin", manager: "Manager" }[r]) || r || "";
}
